import _ from 'lodash';
import React from 'react';
import { decorate, computed, action, runInAction, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';

import { displayError } from '@amzn/base-ui/dist/helpers/notification';

import CopyToClipboard from '../../helpers/CopyToClipboard';
import RdpFile from '../../helpers/RdpFile';

const openWindow = (url, windowFeatures) => {
  return window.open(url, '_blank', windowFeatures);
};

class ScEnvironmentRdpConnectionRow extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.windowsRdpInfo = undefined;
      this.processingGetInfo = false;
      this.processingGetConnection = false;
      this.showPassword = false;
      this.processingId = undefined;
      this.rdpExpanded = false;
      this.dcvExpanded = false;
      this.dcvWarningDismissed = false;
    });
  }

  get isAppStreamEnabled() {
    return process.env.REACT_APP_IS_APP_STREAM_ENABLED === 'true';
  }

  get environment() { return this.props.scEnvironment; }
  get envsStore() { return this.props.scEnvironmentsStore; }
  getConnectionStore() { return this.envsStore.getScEnvConnectionStore(this.environment.id); }

  get connections() { return this.environment.getConnections(item => item.scheme === 'rdp'); }
  get connection() { return _.find(this.connections, ['id', this.connectionId]) || {}; }
  get connectionId() { return this.props.connectionId; }

  get networkInterfaces() {
    const entries = _.get(this.windowsRdpInfo, 'networkInterfaces');
    if (_.isEmpty(entries)) return [];
    const result = [];
    _.forEach(entries, item => {
      if (item.publicDnsName) result.push({ value: item.publicDnsName, type: 'dns', scope: 'public', info: 'Public' });
      if (item.privateIp) result.push({ value: item.privateIp, type: 'ip', scope: 'private', info: 'Private' });
    });
    return result;
  }

  getDcvUrl(useClientProtocol) {
    if (!this.windowsRdpInfo) return new Error("There's no RDP info");
    const publicNetwork = this.networkInterfaces.find(ni => ni.info === 'Public');
    if (!publicNetwork) return new Error('Public DNS not available. Please try RDP instead.');
    let url = `https://${publicNetwork.value}:8443`;
    if (useClientProtocol) url = url.replace('https://', 'dcv://');
    return url;
  }

  handleRdpExpanded = () => { runInAction(() => { this.rdpExpanded = !this.rdpExpanded; }); };
  handleDcvExpanded = () => { runInAction(() => { this.dcvExpanded = !this.dcvExpanded; }); };
  toggleShowPassword = () => { this.showPassword = !this.showPassword; };

  handleGetInfo = async () => {
    const store = this.getConnectionStore();
    runInAction(() => { this.windowsRdpInfo = undefined; this.showPassword = false; this.processingGetInfo = true; });
    try {
      const result = await store.getWindowsRdpInfo(this.connectionId);
      runInAction(() => { this.windowsRdpInfo = result || {}; });
    } catch (error) { displayError(error); }
    finally { runInAction(() => { this.processingGetInfo = false; }); }
  };

  handleDownloadRdpFile = async () => {
    if (!this.windowsRdpInfo) await this.handleGetInfo();
    if (!this.windowsRdpInfo) { displayError(new Error('Unable to retrieve RDP information')); return; }
    const publicNetwork = this.networkInterfaces.find(ni => ni.info === 'Public');
    if (!publicNetwork) { displayError(new Error('Public DNS not available')); return; }
    const rdpFileContents = RdpFile({ publicHostname: publicNetwork.value, username: ".\\Administrator" });
    if (rdpFileContents) {
      const blob = new Blob([rdpFileContents], { type: 'application/x-rdp' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = 'race-workspace.rdp';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else { displayError(new Error('Unable to generate RDP file')); }
  };

  handleOpenDcv = action(async (useClientProtocol) => {
    let url = this.windowsRdpInfo ? this.getDcvUrl(useClientProtocol) : null;
    const urlLoaded = !!url;
    let newWindow;
    runInAction(() => { this.processingGetConnection = true; });
    try {
      newWindow = window.open(`${window.location.origin}/${useClientProtocol ? 'dcv' : 'transit'}.html${url ? `?redirect=${url}` : ''}`, '_blank');
      if (!newWindow || newWindow.closed) throw new Error('Popup was blocked. Please allow popups for this site.');
      if (!this.windowsRdpInfo) await this.handleGetInfo();
      if (!urlLoaded && this.windowsRdpInfo && newWindow) {
        url = this.getDcvUrl(useClientProtocol);
        if (url instanceof Error) throw url;
        newWindow.postMessage({ redirect: url }, window.location.origin);
      }
    } catch (error) { if (newWindow) newWindow.close(); displayError(error); }
    finally { runInAction(() => { this.processingGetConnection = false; }); }
  });

  handleOpenDcvClient = action(async () => { await this.handleOpenDcv(true); });
  handleOpenDcvBrowser = action(async () => { await this.handleOpenDcv(false); });

  handleAppStreamConnect = id => action(async () => {
    try {
      runInAction(() => { this.processingGetConnection = true; });
      const store = this.getConnectionStore();
      const urlObj = await store.createConnectionUrl(id);
      if (urlObj.url) { const tab = openWindow('about:blank'); tab.location = urlObj.url; }
      else throw Error('AppStream URL was not returned');
    } catch (error) { displayError(error); }
    finally { runInAction(() => { this.processingId = ''; this.processingGetConnection = false; }); }
  });

  render() {
    const item = this.connection;
    const processing = this.processingGetInfo;

    return (
      <div>
        <div className="connection-row">
          <span className="connection-row-name">{item.name || 'Connect'}</span>
          <div className="connection-row-actions">
            <button className="connection-btn connection-btn-primary" disabled={processing} onClick={this.handleGetInfo}>
              {processing ? <Icon name="spinner" loading style={{ margin: 0, lineHeight: 1, height: '1em' }} /> : <Icon name="key" style={{ margin: 0, lineHeight: 1, height: '1em' }} />}
              Get Password
            </button>
            <button className="connection-btn connection-btn-secondary" onClick={this.handleDownloadRdpFile}>
              <Icon name="download" style={{ margin: 0, lineHeight: 1, height: '1em' }} /> RDP File
            </button>
            {this.props.isDcvEnabled && (
              <>
                {(() => {
                  const primaryRegion = process.env.REACT_APP_AWS_REGION;
                  const targetRegion = this.environment.targetRegion;
                  const isRemoteRegion = targetRegion && targetRegion !== primaryRegion;
                  if (isRemoteRegion && !this.dcvWarningDismissed) {
                    return (
                      <div className="workspace-alert workspace-alert-warning" style={{ marginTop: '8px', marginBottom: '4px', width: '100%' }}>
                        <Icon name="warning sign" style={{ margin: 0, lineHeight: 1, height: '1em' }} />
                        <span style={{ flex: 1 }}>
                          <strong>Performance notice:</strong> This workspace is in a non-Australian region. DCV streaming may experience higher latency.
                        </span>
                        <button
                          onClick={action(() => { this.dcvWarningDismissed = true; })}
                          aria-label="Dismiss warning"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#92400e', lineHeight: 1, flexShrink: 0 }}
                        >
                          <Icon name="close" style={{ margin: 0, lineHeight: 1, height: '1em' }} />
                        </button>
                      </div>
                    );
                  }
                  return null;
                })()}
                <button className="connection-btn connection-btn-secondary" onClick={this.handleOpenDcvBrowser}>
                  <Icon name="external" style={{ margin: 0, lineHeight: 1, height: '1em' }} /> Open in Browser
                </button>
                <button className="connection-btn connection-btn-secondary" onClick={this.handleOpenDcvClient}>
                  <Icon name="window maximize" style={{ margin: 0, lineHeight: 1, height: '1em' }} /> Open in DCV Client
                </button>
              </>
            )}
          </div>
        </div>
        {this.windowsRdpInfo && this.renderExpanded()}
      </div>
    );
  }

  renderExpanded() {
    const windowsRdpInfo = this.windowsRdpInfo;
    const interfaces = this.networkInterfaces;
    const username = '.\\Administrator';
    const password = windowsRdpInfo.password;
    const showPassword = this.showPassword;
    const publicNetwork = interfaces.find(ni => ni.info === 'Public');

    return (
      <div className="connection-details">
        <div className="connection-details-title">
          <Icon name="desktop" style={{ margin: 0, lineHeight: 1, height: '1em' }} />
          Connection Details
        </div>

        {!this.isAppStreamEnabled && publicNetwork && (
          <div className="credential-item">
            <span className="credential-label">Hostname</span>
            <span className="credential-value">{publicNetwork.value}</span>
            <div className="credential-actions">
              <CopyToClipboard text={publicNetwork.value} />
            </div>
          </div>
        )}

        <div className="credential-item">
          <span className="credential-label">Username</span>
          <span className="credential-value">{username}</span>
          <div className="credential-actions">
            <CopyToClipboard text={username} />
          </div>
        </div>

        <div className="credential-item">
          <span className="credential-label">Password</span>
          <span className="credential-value">{showPassword ? password : '••••••••••••'}</span>
          <div className="credential-actions">
            <button className="connection-btn connection-btn-secondary" style={{ padding: '4px 10px' }} onClick={this.toggleShowPassword}>
              <Icon name={showPassword ? 'eye slash' : 'eye'} style={{ margin: 0, lineHeight: 1, height: '1em' }} />
            </button>
            <CopyToClipboard text={password} />
          </div>
        </div>

        {/* Connection Instructions */}
        <div className="connection-downloads">
          <div className="connection-downloads-title">Connection Instructions</div>
        </div>

        {/* RDP Instructions */}
        <div className="connection-instructions">
          <div
            className={`connection-instructions-header ${this.rdpExpanded ? 'active' : ''}`}
            onClick={this.handleRdpExpanded}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && this.handleRdpExpanded()}
          >
            <Icon name={this.rdpExpanded ? 'chevron down' : 'chevron right'} style={{ margin: 0, lineHeight: 1, height: '1em' }} />
            Connecting via RDP (Recommended)
          </div>
          {this.rdpExpanded && (
            <div className="connection-instructions-content">
              <ol>
                <li>Open your RDP client (or press the RDP File button and skip to step 3):
                  <ul>
                    <li>Windows (Built-in): Press <b>Win+R</b>, type <code>mstsc</code>, and press <b>Enter</b></li>
                    <li>Windows/MacOS (Windows App): Open the &quot;Windows App&quot; application</li>
                  </ul>
                </li>
                <li>Select &quot;Add&quot;, then &quot;PCs&quot; (Windows App only).</li>
                <li>Enter the provided hostname and username into the client.</li>
                <li>Save and connect. You will be prompted for the provided password.</li>
              </ol>
            </div>
          )}
        </div>

        {/* DCV Instructions */}
        {this.props.isDcvEnabled && (
          <div className="connection-instructions" style={{ marginTop: '8px' }}>
            <div
              className={`connection-instructions-header ${this.dcvExpanded ? 'active' : ''}`}
              onClick={this.handleDcvExpanded}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && this.handleDcvExpanded()}
            >
              <Icon name={this.dcvExpanded ? 'chevron down' : 'chevron right'} style={{ margin: 0, lineHeight: 1, height: '1em' }} />
              Connecting via DCV
            </div>
            {this.dcvExpanded && (
              <div className="connection-instructions-content">
                <ol>
                  <li>Select DCV Browser or Client (please install the client before using the client button).</li>
                  <li>Enter the provided username and password, and press &quot;Connect&quot;.</li>
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Download links */}
        <div className="connection-downloads">
          <div className="connection-downloads-title">Client Downloads</div>
          <a href="https://apps.microsoft.com/detail/9n1f85v9t8bn" target="_blank" rel="noopener noreferrer">
            <Icon name="windows" style={{ margin: 0, lineHeight: 1, height: '1em' }} /> Windows App (Windows)
          </a>
          <a href="https://apps.apple.com/us/app/windows-app/id1295203466" target="_blank" rel="noopener noreferrer">
            <Icon name="apple" style={{ margin: 0, lineHeight: 1, height: '1em' }} /> Windows App (MacOS)
          </a>
          <a href="https://www.amazondcv.com/" target="_blank" rel="noopener noreferrer">
            <Icon name="cloud download" style={{ margin: 0, lineHeight: 1, height: '1em' }} /> Amazon DCV Client
          </a>
        </div>

        {/* AppStream connect button */}
        {this.isAppStreamEnabled && (
          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="connection-btn connection-btn-primary"
              onClick={this.handleAppStreamConnect(this.connectionId)}
              disabled={this.processingGetConnection}
            >
              {this.processingGetConnection ? <Icon name="spinner" loading style={{ margin: 0, lineHeight: 1, height: '1em' }} /> : <Icon name="play" style={{ margin: 0, lineHeight: 1, height: '1em' }} />}
              Connect via AppStream
            </button>
          </div>
        )}
      </div>
    );
  }
}

decorate(ScEnvironmentRdpConnectionRow, {
  envsStore: computed,
  environment: computed,
  connections: computed,
  connection: computed,
  connectionId: computed,
  networkInterfaces: computed,
  processingId: observable,
  windowsRdpInfo: observable,
  processingGetInfo: observable,
  processingGetConnection: observable,
  showPassword: observable,
  rdpExpanded: observable,
  dcvExpanded: observable,
  dcvWarningDismissed: observable,
  handleGetInfo: action,
  handleOpenDcv: action,
  handleOpenDcvClient: action,
  handleOpenDcvBrowser: action,
  toggleShowPassword: action,
});

export default inject('scEnvironmentsStore')(withRouter(observer(ScEnvironmentRdpConnectionRow)));
